<template>
    <div class="search-results" :class="{ loading: loading }" @blur="resetTabIndex">
        <div v-if="loading" class="loading-block">
            <UISpinner></UISpinner> <span class="pl-2">{{ translate('search.loading') }}...</span>
        </div>
        <template v-if="totalResults === 0">
            <div class="check-box-body mx-3 mx-md-0">
                <p class="mb-0">{{ translate('search.empty.text') }}</p>
                <div class="mt-2">
                    <UIIcon :name="['fa', 'check-circle']" class="icon ml-1"></UIIcon>
                    &nbsp;
                    <label class="label">{{ translate('search.spelling') }}</label>
                </div>
                <div>
                    <UIIcon :name="['fa', 'check-circle']" class="icon ml-1"></UIIcon>
                    &nbsp;
                    <label class="label">{{ translate('search.term') }}</label>
                </div>
                <div>
                    <UIIcon :name="['fa', 'check-circle']" class="icon ml-1"></UIIcon>
                    &nbsp;
                    <label class="label">{{ translate('search.switch') }}</label>
                </div>
                <div class="text">
                    <p v-html="translate('search.link.email')"></p>
                </div>
            </div>
            <div class="mx-3 mx-md-0">
                <h2>{{ translate('search.catalog') }}</h2>
                <div class="category-row d-flex">
                    <div v-for="category in activeCategories" :key="category.slug" class="card">
                        <HomepageCard
                            :link="$category.getPath(category.slug)"
                            :title="translate(`category.${category.slug}.name`)"
                            :image="getImage(category)"
                            :colour="getColour(category)"
                            :variant="activeCategories.length <= 5 ? 'large' : 'small'"
                            class="category-search-card"
                        ></HomepageCard>
                    </div>
                </div>
            </div>
        </template>
        <div v-for="(product, index) in products" v-else :key="index" class="product-wrapper">
            <!-- results are comint from elasticSearch if they have the property modelType -->
            <template v-if="product.modelType">
                <ProductBrandItem v-if="product.modelType === 'brand'" :brand="product"></ProductBrandItem>
                <ProductItem v-else-if="product.modelType === 'product'" :product="product"></ProductItem>
                <SearchResultPage v-else-if="product.modelType === 'page'" :item="product"></SearchResultPage>
                <SearchResultText v-else :item="product"></SearchResultText>
            </template>
            <template v-else>
                <!-- else it is the category page -->
                <ProductItem v-if="product.productId" :product="product"></ProductItem>
                <ProductBrandItem v-else-if="product.brandName" :brand="product"></ProductBrandItem>
                <SearchResultText v-else :item="product"></SearchResultText>
            </template>
        </div>
    </div>
</template>

<script>
import { UISpinner, UIIcon } from '@dundle/ui/components';
import { mapGetters } from 'vuex';
import { HomepageCard } from '~/components/homepage';
import { ProductItem, ProductBrandItem } from '~/components/product';
import { SearchResultText, SearchResultPage } from '~/components/search';
import TranslationMixin from '~/mixins/TranslationMixin';
import PageMixin from '~/mixins/PageMixin';

export default {
    components: {
        ProductItem,
        ProductBrandItem,
        SearchResultPage,
        UISpinner,
        SearchResultText,
        HomepageCard,
        UIIcon,
    },

    mixins: [TranslationMixin],

    props: ['total', 'loading', 'products', 'query'],
    async asyncData(ctx) {
        const { store } = ctx;
        const pageData = PageMixin.fetchData(ctx);
        const categories = store.getters['data/getCategories'].map(category => {
            return {
                name: category.slug,
            };
        });
        return {
            ...(await pageData),
            categories,
        };
    },
    computed: {
        ...mapGetters({
            categories: 'data/getCategories',
            getActiveCategories: 'data/getActiveCategories',
        }),

        activeCategories() {
            return this.categories.filter(category => this.getActiveCategories.includes(category.slug));
        },
        totalResults() {
            return this.total;
        },
    },
    methods: {
        resetTabIndex(el) {
            el.target.removeAttribute('tabindex');
        },
        navigateToSupport() {
            this.$router.push(this.$locale.path('support'));
        },
        getImage(category) {
            return this.$store.getters['data/getCategoryImage'](category.slug);
        },
        getColour(category) {
            return this.$store.getters['data/getCategoryColour'](category.slug);
        },
    },
};
</script>

<style lang="scss" scoped>
.search-results {
    border-top: 1px solid $color-grey-3;

    &:focus {
        outline: none;
    }

    &.loading {
        position: relative;

        &::before {
            content: '';
            position: absolute;
            background-color: rgba(255, 255, 255, 0.8);
            width: calc(100% + 0.4rem);
            height: 100%;
            top: 0;
            left: -0.4rem;
            z-index: 1;
        }

        .loading-block {
            position: absolute;
            top: 1rem;
            width: 8rem;
            left: calc(50% - 4rem);
            z-index: 2;
        }
    }
    .check-box-body {
        margin: 1rem;
        .text {
            margin: 15px 8px;
        }
        .icon {
            color: $color-validation;
            font-size: 1.25rem !important;
        }
    }

    .top-spacing {
        margin-top: 3rem;

        @media screen and (min-width: 768px) {
            margin-top: 1rem;
        }

        &.small {
            margin-top: 4.333rem;
        }

        &.content {
            margin-top: 1rem;

            @media screen and (min-width: 768px) {
                margin-top: 4rem;
            }
        }
    }

    .product-wrapper {
        &:focus {
            outline: none;
        }
        @media screen and (max-width: 767px) {
            &:last-child {
                padding-bottom: 10px;
                background-color: $color-grey-1;
            }
        }
    }
    .category-row {
        justify-content: space-between;
        overflow-x: scroll;
        flex-wrap: nowrap;

        scrollbar-width: none;
        &::-webkit-scrollbar {
            display: none; /* Safari and Chrome */
        }

        .category-search-card ::v-deep .card {
            margin-right: 1rem;
        }

        .category-search-card ::v-deep .card .image {
            width: 100%;
            height: auto;
        }
    }

    @media screen and (max-width: 767px) {
        margin-left: -15px;
        margin-right: -15px;

        .x-product-item {
            border-top: 10px solid $color-grey-1;
        }
    }
}
</style>

<i18n src="@dundle/locale/data/search.json"></i18n>
<i18n src="@dundle/locale/data/support.json"></i18n>
<i18n src="@dundle/locale/data/categories.json"></i18n>
